import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCart as apiGetCart,
  updateCart as apiUpdateCart,
} from "../services/cartService";
import * as wishlistService from "../services/wishlistService";
import toast from "react-hot-toast";
import getOptimizedUrl from '../utils/imageUtils';
import { isAccessory, isBottomProductType } from '../utils/productUtils';
import {
  CUSTOM_LENGTH_RANGE,
  CUSTOM_WAIST_RANGE,
  DEFAULT_CUSTOM_LENGTH,
  DEFAULT_CUSTOM_WAIST,
  formatCustomSizeLabel,
  parseCustomMeasurements,
} from "../utils/customSizeUtils";
import { calculateShippingQuoteFromSubtotal } from "../utils/shippingUtils";

const CUSTOM_LENGTH_CHART = {
  height: ["5'' - 5'2", "5'3 - 5'4", "5'5 - 5'6", "5'7 - 5'8", "5'9 - 5'10", "5'10 - 6''"],
  recommended: [39, 40, 40, 41, 42, 43],
};

const isFreeSizeValue = (size = "") => {
  const normalized = String(size || "").trim().toLowerCase().replace(/\s+/g, "");
  return normalized === "freesize" || normalized === "onesize";
};

export default function CartSidebar() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [items, setItems] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [wishlistLoadingByProduct, setWishlistLoadingByProduct] = useState({});
  const [customSizeModal, setCustomSizeModal] = useState({ open: false, productId: null, oldSize: null });
  const [customLength, setCustomLength] = useState(DEFAULT_CUSTOM_LENGTH);
  const [customWaist, setCustomWaist] = useState(DEFAULT_CUSTOM_WAIST);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  const loadFromServer = async ({ showLoader = false } = {}) => {
    if (showLoader) setLoading(true);
    try {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (!user || !token) {
        setItems([]);
        return;
      }
      const serverCart = await apiGetCart();
      if (Array.isArray(serverCart)) {
        const mapped = serverCart.map((i) => ({
          productId: i.product._id,
          product: i.product.product,
          image: i.product.images?.[0] || "",
          size: i.size || "",
          customSize: i.customSize || "",
          quantity: i.qty ?? i.quantity ?? 1,
          price: i.price ?? i.product.discountedPrice ?? 0,
          originalPrice: i.originalPrice ?? i.product.mrp ?? null,
          discountPercent: i.discountPercent ?? 0,
          productType: i.product.productType,
        }));
        setItems(mapped);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Failed to load server cart', err);
      setItems([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadFromServer({ showLoader: false });
    
    // Load wishlist
    const loadWishlist = async () => {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (user && token) {
        try {
          const data = await wishlistService.getWishlist();
          setWishlistIds(data.map(item => item.product._id));
        } catch (error) {
          console.error('Failed to load wishlist:', error);
        }
      } else {
        setWishlistIds([]);
      }
    };
    
    loadWishlist();
    
    const onToggle = (e) => {
      const open = e?.detail?.open ?? !isOpen;
      if (open) {
        setMounted(true);
        setClosing(false);
        setIsOpen(true);
        loadFromServer({ showLoader: true });
      } else {
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => {
          setMounted(false);
          setClosing(false);
        }, 300);
      }
    };
    const onUpdated = () => loadFromServer({ showLoader: false });

    window.addEventListener("cart:toggle", onToggle);
    window.addEventListener("cart:updated", onUpdated);

    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (user && token) {
      loadFromServer({ showLoader: false });
    }

    return () => {
      window.removeEventListener("cart:toggle", onToggle);
      window.removeEventListener("cart:updated", onUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subtotal = items.reduce(
    (s, i) => s + (i.price || 0) * (i.quantity || 0),
    0
  );
  const shippingPreview = calculateShippingQuoteFromSubtotal(subtotal);

  const syncToServer = async (next) => {
    if (isMutating) return false;
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!user || !token) {
      try {
        window.dispatchEvent(new CustomEvent("signup:toggle", { detail: { open: true } }));
      } catch (e) {}
      toast.error('Please login to modify your cart');
      return false;
    }
    setIsMutating(true);
    try {
      const serverRes = await apiUpdateCart(
        (next || []).map((i) => ({
          product: i.productId,
          qty: i.quantity,
          size: i.size,
          customSize: i.customSize || '',
          price: i.price,
        }))
      );
      if (Array.isArray(serverRes)) {
        const mapped = serverRes.map((i) => ({
          productId: i.product._id,
          product: i.product.product,
          image: i.product.images?.[0] || "",
          size: i.size || "",
          customSize: i.customSize || "",
          quantity: i.qty ?? i.quantity ?? 1,
          price: i.price ?? i.product.discountedPrice ?? 0,
          originalPrice: i.originalPrice ?? i.product.mrp ?? null,
          discountPercent: i.discountPercent ?? 0,
          productType: i.product.productType,
        }));
        setItems(mapped);
        window.dispatchEvent(new Event("cart:updated"));
      }
      return true;
    } catch (e) {
      console.error("sync cart failed", e);
      const message =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to update cart";
      toast.error(message);
      return false;
    } finally {
      setIsMutating(false);
    }
  };

  const updateQty = async (productId, size, qty) => {
    if (loading || isMutating) return;
    const next = items
      .map((it) =>
        it.productId === productId && it.size === size
          ? { ...it, quantity: qty }
          : it
      )
      .filter((i) => i.quantity > 0);
    // Sync to server and refresh from canonical server state
    await syncToServer(next);
  };

  const updateSize = async (productId, oldSize, newSize, productType) => {
    if (loading || isMutating) return;
    const currentItem = items.find((it) => it.productId === productId && it.size === oldSize);
    if (newSize === "Custom" && !isBottomProductType(productType, currentItem?.product)) {
      toast.error("Custom size is only available for bottoms");
      return;
    }
    // If clicking Custom, open modal to get custom text
    if (newSize === "Custom") {
      // Check if current item has existing customSize
      const existingCustomText = currentItem?.customSize || "";
      const parsed = parseCustomMeasurements(existingCustomText);
      
      setCustomLength(parsed.length);
      setCustomWaist(parsed.waist);
      setCustomSizeModal({ open: true, productId, oldSize });
      return;
    }
    
    if (oldSize === newSize) return;
    let next = [...items];
    const idx = next.findIndex(
      (it) => it.productId === productId && it.size === oldSize
    );
    if (idx === -1) return;
    const existingIdx = next.findIndex(
      (it) => it.productId === productId && it.size === newSize
    );
    if (existingIdx !== -1) {
      next[existingIdx] = {
        ...next[existingIdx],
        quantity: (next[existingIdx].quantity || 0) + (next[idx].quantity || 0),
      };
      next.splice(idx, 1);
    } else {
      next[idx] = { ...next[idx], size: newSize, customSize: '' };
    }
    // Sync to server and refresh from canonical server state
    await syncToServer(next);
  };

  const handleCustomSizeSubmit = async () => {
    if (loading || isMutating) return;
    const { productId, oldSize } = customSizeModal;
    const customValue = formatCustomSizeLabel(customLength, customWaist);
    
    let next = [...items];
    const idx = next.findIndex(
      (it) => it.productId === productId && it.size === oldSize
    );
    if (idx === -1) return;
    
    const existingIdx = next.findIndex(
      (it) => it.productId === productId && it.size === "Custom" && it.customSize === customValue
    );
    
    if (existingIdx !== -1 && existingIdx !== idx) {
      // Merge with existing custom with same text
      next[existingIdx] = {
        ...next[existingIdx],
        quantity: (next[existingIdx].quantity || 0) + (next[idx].quantity || 0),
      };
      next.splice(idx, 1);
    } else {
      // Update current item
      next[idx] = { ...next[idx], size: "Custom", customSize: customValue };
    }
    
    // Sync to server and refresh from canonical server state
    const ok = await syncToServer(next);
    if (!ok) return;
    
    // Close modal and reset
    setCustomSizeModal({ open: false, productId: null, oldSize: null });
    setCustomLength(DEFAULT_CUSTOM_LENGTH);
    setCustomWaist(DEFAULT_CUSTOM_WAIST);
    toast.success('Custom size updated');
  };

  const removeItem = async (productId, size) => {
    if (loading || isMutating) return;
    const next = items.filter(
      (it) => !(it.productId === productId && it.size === size)
    );
    // Sync to server and refresh from canonical server state
    const ok = await syncToServer(next);
    if (ok) toast.success("Removed from cart");
  };

  const toggleWishlist = async (productId, size) => {
    if (wishlistLoadingByProduct[productId]) return;
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!user || !token) {
      toast.error('Please login to add items to wishlist');
      try {
        window.dispatchEvent(
          new CustomEvent("signup:toggle", {
            detail: {
              open: true,
              returnToCurrent: true,
              returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          })
        );
      } catch (e) {}
      return;
    }
    
    const isCurrentlyInWishlist = wishlistIds.includes(productId);
    
    setWishlistLoadingByProduct((prev) => ({ ...prev, [productId]: true }));
    try {
      if (isCurrentlyInWishlist) {
        // Remove by server entry (prefer product-only removal)
        const serverList = await wishlistService.getWishlist();
        const entry = serverList.find(it => String(it.product?._id) === String(productId));
        const sizeToRemove = entry?.size;
        await wishlistService.removeFromWishlist(productId, sizeToRemove);
        const updated = await wishlistService.getWishlist();
        setWishlistIds(updated.map(i => i.product._id));
        toast.success('Removed from wishlist');
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: updated } }));
      } else {
        // When adding from cart, do not attach size — keep wishlist entries product-only
        await wishlistService.addToWishlist(productId);
        const updated = await wishlistService.getWishlist();
        setWishlistIds(updated.map(i => i.product._id));
        toast.success('Added to wishlist!');
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: updated } }));
      }
    } catch (error) {
      console.error('Wishlist error:', error);
      const backendMsg = error?.response?.data?.message;
      if (!backendMsg) toast.error('Failed to update wishlist');
    } finally {
      setWishlistLoadingByProduct((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleCheckout = () => {
    if (items.length === 0) return;

    // Check if user is logged in
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!user || !token) {
      toast.error("Please login to continue to checkout");
      try {
        window.dispatchEvent(
          new CustomEvent("signup:toggle", { detail: { open: true } })
        );
      } catch (e) {}
      return;
    }

    // Close cart sidebar
    setClosing(true);
    setIsOpen(false);
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, 300);

    // Navigate to checkout page
    navigate("/checkout");
  };

  return (
    <>
      {mounted && (
        <>
          <div
            className="fixed inset-0 bg-black/40 font-bdogrotesk z-[10000]"
            onClick={() => {
              setClosing(true);
              setIsOpen(false);
              setTimeout(() => setMounted(false), 300);
            }}
          />

          <div
            className={`fixed top-0 right-0 bottom-0 w-full max-w-lg bg-white z-[10001] shadow-2xl flex flex-col drawer-panel ${
              closing ? "drawer-slide-out" : "drawer-slide-in"
            }`}
          >
            <div className="p-2 flex items-center justify-between">
              <h3 className="text-base text-black font-bdogrotesk">
                Cart{" "}
                <span className="text-base font-bdogrotesk text-black">
                  {items.length}
                </span>
              </h3>
              <button
                onClick={() => {
                  setClosing(true);
                  setIsOpen(false);
                  setTimeout(() => setMounted(false), 300);
                }}
                className="text-base text-black font-bdogrotesk"
              >
                Close
              </button>
            </div>

            <div className="p-2 sm:p-3 flex-1 overflow-y-auto no-scrollbar">
              {loading ? (
                <div className="text-center text-black py-40 font-medium text-sm sm:text-base">
                  Loading cart...
                </div>
              ) : items.length === 0 ? (
                <div className="text-center text-black py-48 font-bold text-2xl">
                  Your cart is empty
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((it) => (
                    <div key={`${it.productId}-${it.size}`}>
                      <div className="flex gap-3 sm:gap-4 items-start">
                        <div 
                          className="w-28 h-36 sm:w-40 sm:h-48 bg-white flex-shrink-0 overflow-hidden cursor-pointer"
                          onClick={() => {
                            setClosing(true);
                            setIsOpen(false);
                            setTimeout(() => setMounted(false), 300);
                            navigate(`/product/${it.productId}`);
                          }}
                        >
                          {it.image ? (
                            <img
                              src={getOptimizedUrl(it.image || '', 800)}
                              alt=""
                              className="w-full h-full object-cover bg-white"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              No Image
                            </div>
                          )}
                        </div>
                        <div className="flex-1 flex flex-col min-h-[9rem] sm:min-h-[12rem] min-w-0">
                          <div className="flex justify-between items-start">
                            <div className="w-full sm:max-w-none min-w-0 pr-2 sm:pr-0">
                              <div className="font-medium font-bdogrotesk text-sm sm:text-base leading-snug break-words">
                                {it.product}
                              </div>
                              <div className="flex items-baseline gap-2 sm:gap-3 text-xs text-gray-500 mt-1.5 sm:mt-2 overflow-x-auto sm:overflow-visible no-scrollbar whitespace-nowrap sm:whitespace-normal sm:flex-wrap pr-1">
                                {isAccessory(it.productType) || isFreeSizeValue(it.size) ? (
                                  <div className="text-sm font-semibold text-black">
                                    Freesize
                                  </div>
                                ) : (
                                  <>
                                    {[
                                      "XXS",
                                      "XS",
                                      "S",
                                      "M",
                                      "L",
                                      "XL",
                                      "XXL",
                                      ...(isBottomProductType(it.productType, it.product) ? ["Custom"] : []),
                                    ].map((sz) => {
                                      const isCustomSize = it.size === "Custom";
                                      const isSelected = sz === "Custom" ? isCustomSize : it.size === sz;
                                      
                                      return (
                                        <button
                                          key={sz}
                                          onClick={() =>
                                            updateSize(it.productId, it.size, sz, it.productType)
                                          }
                                          disabled={loading || isMutating}
                                          className={`shrink-0 sm:shrink text-xs sm:text-sm pb-1.5 sm:pb-2 ${
                                            isSelected
                                              ? "font-semibold text-black border-b-2 border-solid border-black"
                                              : "text-gray-400"
                                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                                        >
                                          {sz}
                                        </button>
                                      );
                                    })}
                                  </>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                                <div className="flex items-center border border-black sm:border-2">
                                  <button
                                    onClick={() =>
                                      updateQty(
                                        it.productId,
                                        it.size,
                                        Math.max(0, (it.quantity || 1) - 1)
                                      )
                                    }
                                    disabled={loading || isMutating}
                                    className="px-1.5 sm:px-2 py-1 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    -
                                  </button>
                                  <div className="px-1.5 sm:px-2 text-xs sm:text-sm">
                                    {it.quantity}
                                  </div>
                                  <button
                                    onClick={() =>
                                      updateQty(
                                        it.productId,
                                        it.size,
                                        (it.quantity || 1) + 1
                                      )
                                    }
                                    disabled={loading || isMutating}
                                    className="px-1.5 sm:px-2 py-1 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    +
                                  </button>
                                </div>
                                <button
                                  onClick={() =>
                                    removeItem(it.productId, it.size)
                                  }
                                  disabled={loading || isMutating}
                                  className="px-3 sm:px-4 py-1 border border-black sm:border-2 sm:border-solid sm:border-black text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                              <button
                                onClick={() => toggleWishlist(it.productId)}
                                disabled={loading || isMutating || wishlistLoadingByProduct[it.productId]}
                                className="shrink-0 pr-1 sm:pr-3 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                              {wishlistIds.includes(it.productId) ? (
                                <svg
                                  width="26"
                                  height="26"
                                  viewBox="0 0 19 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                                    fill="#F31717"
                                    stroke="#F31717"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="26"
                                  height="26"
                                  viewBox="0 0 19 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                                    fill="white"
                                    stroke="black"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                          <div className="mt-auto flex items-end gap-2 pb-1 pt-2 sm:pt-0">
                            <div className="text-xs sm:text-sm text-gray-400 line-through">
                              ₹
                              {Math.round(
                                (it.originalPrice ??
                                  Math.round((it.price || 0) * 1.5)) *
                                  (it.quantity || 0)
                              )}
                            </div>
                            <div className="text-xs sm:text-sm font-semibold">
                              ₹{(it.price || 0) * (it.quantity || 0)}
                              <span className="text-red-500 text-xs">
                                ({it.discountPercent ?? 0}% Off)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr className="my-4 border-black" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-4 border-t">
                <div className="space-y-2 mb-4">
                  {/* <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Subtotal ({items.length} items)</span>
                    <span className="font-medium">₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Tax (0% GST)</span>
                    <span className="font-medium">₹{taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium">{shippingPrice === 0 ? 'Free' : `₹${shippingPrice}`}</span>
                  </div>
                  <hr className="my-2" /> */}
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bdogrotesk font-semibold">
                      Subtotal {items.length}
                    </span>
                    <span className="text-base font-bdogrotesk font-semibold">
                      ₹{subtotal.toFixed(0)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {shippingPreview.qualifiesForFreeShipping
                      ? "Free shipping unlocked"
                      : `Add items worth ₹${Math.ceil(shippingPreview.amountForFreeShipping || 0).toLocaleString("en-IN")} to unlock free shipping`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={items.length === 0 || loading || isMutating}
                  className="group w-full bg-black text-white px-3 py-3 flex items-center justify-between text-base font-medium tracking-wide disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <div className="relative h-5 overflow-hidden">
                    <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                      <span className="flex font-bdogrotesk h-5 items-center">
                        Continue to Checkout
                      </span>
                      <span className="flex h-5 font-bdogrotesk items-center">
                        Continue to Checkout
                      </span>
                    </div>
                  </div>
                </button>
                {/* <div className="text-xs text-gray-500 text-center mt-2">
                  Prices include 5% GST
                </div> */}
              </div>
            )}
          </div>
        </>
      )}

      {/* Custom Size Modal */}
      {customSizeModal.open && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[10002]"
            onClick={() => {
              setCustomSizeModal({ open: false, productId: null, oldSize: null });
              setCustomLength(DEFAULT_CUSTOM_LENGTH);
              setCustomWaist(DEFAULT_CUSTOM_WAIST);
            }}
          />
          <div
            className="fixed inset-0 z-[10003] flex items-center justify-center p-3 sm:p-4"
            onClick={() => {
              setCustomSizeModal({ open: false, productId: null, oldSize: null });
              setCustomLength(DEFAULT_CUSTOM_LENGTH);
              setCustomWaist(DEFAULT_CUSTOM_WAIST);
            }}
          >
            <div
              className="bg-white w-full max-w-[96vw] sm:max-w-4xl md:max-w-5xl max-h-[90vh] overflow-y-auto p-3 sm:p-6 md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-end mb-2">
                <button
                  onClick={() => {
                    setCustomSizeModal({ open: false, productId: null, oldSize: null });
                    setCustomLength(DEFAULT_CUSTOM_LENGTH);
                    setCustomWaist(DEFAULT_CUSTOM_WAIST);
                  }}
                  className="text-sm font-medium hover:text-gray-600"
                >
                  Close
                </button>
              </div>

              <div className="w-full overflow-x-auto pb-1">
                <table className="w-full min-w-[560px] border-collapse">
                  <tbody>
                    <tr className="border-b border-black/60">
                      <td className="py-2 pr-2 sm:pr-3 text-xs sm:text-sm font-medium">Height</td>
                      {CUSTOM_LENGTH_CHART.height.map((h) => (
                        <td key={h} className="py-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium text-left whitespace-nowrap">{h}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-black/60">
                      <td className="py-2 pr-2 sm:pr-3 text-xs sm:text-sm font-medium leading-tight">Recommended<br/>Length</td>
                      {CUSTOM_LENGTH_CHART.recommended.map((len, idx) => (
                        <td key={len} className="py-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium text-left whitespace-nowrap">{idx === CUSTOM_LENGTH_CHART.recommended.length - 1 ? `${len}+` : len}"</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mt-4 text-xs text-gray-600 lg:hidden">Select length and waist</p>
              <div className="mt-2 lg:mt-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto lg:overflow-visible">
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <div className="text-xs sm:text-sm font-medium whitespace-nowrap">
                      <span className="lg:hidden">Length</span>
                      <span className="hidden lg:inline">Custom Length</span>
                    </div>
                    <div className="inline-flex items-center border border-black h-9 sm:h-10 w-fit">
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomLength((prev) => Math.max(CUSTOM_LENGTH_RANGE.min, prev - 1))}
                        disabled={loading || isMutating}
                      >
                        -
                      </button>
                      <div className="w-12 sm:w-16 text-center text-xs sm:text-sm font-medium">{customLength}"</div>
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomLength((prev) => Math.min(CUSTOM_LENGTH_RANGE.max, prev + 1))}
                        disabled={loading || isMutating}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <div className="text-xs sm:text-sm font-medium whitespace-nowrap">
                      <span className="lg:hidden">Waist</span>
                      <span className="hidden lg:inline">Custom Waist</span>
                    </div>
                    <div className="inline-flex items-center border border-black h-9 sm:h-10 w-fit">
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomWaist((prev) => Math.max(CUSTOM_WAIST_RANGE.min, prev - 1))}
                        disabled={loading || isMutating}
                      >
                        -
                      </button>
                      <div className="w-12 sm:w-16 text-center text-xs sm:text-sm font-medium">{customWaist}"</div>
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomWaist((prev) => Math.min(CUSTOM_WAIST_RANGE.max, prev + 1))}
                        disabled={loading || isMutating}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCustomSizeSubmit}
                  disabled={loading || isMutating}
                  className="px-2.5 sm:px-4 h-9 sm:h-10 bg-black text-white text-[11px] sm:text-sm font-medium lg:ml-auto whitespace-nowrap w-full lg:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isMutating ? 'Updating...' : 'Order Custom Size'}
                </button>
              </div>

              <p className="mt-3 text-xs sm:text-sm font-medium leading-snug">
                Customisations are <span className="font-semibold">free of cost</span>! | Customised pieces are not applicable for return/exchange as they're made just for you!
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
